import { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

const FOUNDER_EMAIL = "ansh@thelocaljewel.com";
const BRANDS = [
  { id: "thelocaljewel", name: "TheLocalJewel" },
  { id: "justlabs", name: "Justlabs" }
];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBrand, setActiveBrand] = useState(null);
  const [showMoneyGoalPopup, setShowMoneyGoalPopup] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  // Initialize active brand when user is loaded
  useEffect(() => {
    if (user) {
      // Try to restore last selected brand from localStorage (for founder only)
      const savedBrand = localStorage.getItem('activeBrand');
      if (user.email === FOUNDER_EMAIL && savedBrand && BRANDS.some(b => b.id === savedBrand)) {
        setActiveBrand(savedBrand);
      } else {
        // Default to user's assigned brand
        setActiveBrand(user.brand || 'thelocaljewel');
      }
      
      // Show money goal popup if not set (once per session once dismissed)
      if (!user.money_goal && !sessionStorage.getItem('moneyGoalDismissed')) {
        setShowMoneyGoalPopup(true);
      }
    }
  }, [user]);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('activeBrand');
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(userData);
    // Set initial brand
    setActiveBrand(userData.brand || 'thelocaljewel');
    return userData;
  };

  const register = async (email, password, name, role = 'agency', brand = 'thelocaljewel') => {
    const response = await api.post('/auth/register', { email, password, name, role, brand });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(userData);
    setActiveBrand(userData.brand || 'thelocaljewel');
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('activeBrand');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setActiveBrand(null);
  };

  const isAdmin = () => user?.role === 'admin';
  const isFounder = () => user?.email === FOUNDER_EMAIL;
  const isIdeaManager = () => user?.role === 'admin' || user?.email === 'rhea@thelocaljewel.com';
  // Users with full access to the eBay listing flow (shared brand Seller Hub).
  const isEbayUser = () => {
    const allowed = [FOUNDER_EMAIL, 'nayan@thelocaljewel.com', 'utkarsh@thelocaljewel.com'];
    return user?.role === 'admin' || allowed.includes(user?.email);
  };
  
  // Switch brand (only for founder)
  const switchBrand = (brandId) => {
    if (user?.email === FOUNDER_EMAIL && BRANDS.some(b => b.id === brandId)) {
      setActiveBrand(brandId);
      localStorage.setItem('activeBrand', brandId);
    }
  };

  // Get the current active brand name for display
  const getActiveBrandName = () => {
    const brand = BRANDS.find(b => b.id === activeBrand);
    return brand?.name || 'TheLocalJewel';
  };

  // Update money goal
  const updateMoneyGoal = async (moneyGoal) => {
    try {
      const response = await api.patch('/auth/money-goal', { money_goal: moneyGoal });
      setUser(response.data);
      setShowMoneyGoalPopup(false);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Get token from localStorage (for direct use in URLs)
  const getToken = () => localStorage.getItem('token');

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      isAdmin,
      isFounder,
      isEbayUser,
      isIdeaManager,
      activeBrand,
      switchBrand,
      getActiveBrandName,
      brands: BRANDS,
      showMoneyGoalPopup,
      setShowMoneyGoalPopup,
      updateMoneyGoal,
      token: localStorage.getItem('token'),
      getToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};
